import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LayoutHeader from '../../../../../layout/LayoutHeader';
import DizeeInput from '../../../../../components/DizeeComponent/DizeeInput';
import { clearFocusSection } from '../../../../../store/focuspage/focuspageSlice';
import { addCustomForFocusThunk } from '../../../../../store/focuspage/focuspageThunk';
import { setErrorAlert, setLoader } from '../../../../../store/alert/alertSlice';
import { uploadImage } from '../../../../../utils/upload';
import Check from '../../../../../svg/Check';
import AddedCustom from '../../../../../components/Focuspage/Type/Custom/AddedCustom';
import OverlayCustom from '../../../../../components/Focuspage/Type/Custom/OverlayCustom';

export default function AddProductContent() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = localStorage.getItem('dizeeToken');
    const custom = useSelector(state => state.focuspage.custom);

    const [sectionTitle, setSectionTitle] = useState('');
    const [currentProduct, setCurrentProduct] = useState({
        secondaryText: '',
        link: '',
        image: null,
        preview: null,
        type: 'product'
    });
    const [productItems, setProductItems] = useState([]);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const isValidUrl = (url) => {
        if (!url) return false;
        const tldPattern = /\.[a-z]{1,}$/i;
        try {
            const urlObj = new URL(url);
            return tldPattern.test(urlObj.hostname);
        } catch {
            return false;
        }
    };

    const isFormComplete = currentProduct.preview;

    const isDoneValid = () => {
        return productItems.length > 0 || isFormComplete;
    };

    const handleLinkChange = (e) => {
        let value = e.target.value;
        if (value && !value.startsWith('http')) {
            value = `https://${value}`;
        }
        setCurrentProduct({ ...currentProduct, link: value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // File size validation (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                dispatch(setErrorAlert('Image size should be less than 5MB'));
                return;
            }

            // File type validation
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                dispatch(setErrorAlert('Please upload a valid image file (JPEG, PNG, GIF, or WEBP)'));
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setCurrentProduct({
                    ...currentProduct,
                    image: file,
                    preview: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    const validateForm = () => {
        // Image is required
        if (!currentProduct.preview) {
            dispatch(setErrorAlert('Please upload an image'));
            return false;
        }

        // Description length validation (if provided)
        if (currentProduct.secondaryText && currentProduct.secondaryText.length > 1500) {
            dispatch(setErrorAlert('Description must be less than 1500 characters'));
            return false;
        }

        // Link validation if provided
        if (currentProduct.link && !isValidUrl(currentProduct.link)) {
            dispatch(setErrorAlert('Please enter a valid URL'));
            return false;
        }

        return true;
    };

    const handleAddAnother = () => {
        if (!validateForm()) {
            return;
        }

        setProductItems([...productItems, {
            ...currentProduct,
            type: 'product',
            sectionTitle: sectionTitle,
            description: currentProduct.secondaryText,
            secondaryText: currentProduct.secondaryText
        }]);

        // Reset form
        setCurrentProduct({
            secondaryText: '',
            link: '',
            image: null,
            preview: null,
            type: 'product'
        });
    };

    const handleEditProduct = () => {
        if (selectedProduct) {
            setCurrentProduct(selectedProduct);
            const updatedProducts = productItems.filter((_, index) => index !== selectedIndex);
            setProductItems(updatedProducts);
            setIsOverlayVisible(false);
        }
    };

    const handleDeleteProduct = () => {
        const updatedProducts = productItems.filter((_, index) => index !== selectedIndex);
        setProductItems(updatedProducts);
        setIsOverlayVisible(false);
    };

    const handleDone = async () => {
        if (!isDoneValid()) {
            dispatch(setErrorAlert('Please add at least one product'));
            return;
        }

        dispatch(setLoader(true));

        try {
            // Get existing content
            let finalContent = [...custom?.content || []];
            // Filter out existing products
            finalContent = finalContent.filter(item => item.type !== 'product');
            
            let contentToSubmit = [...productItems];
            if (isFormComplete) {
                contentToSubmit.push(currentProduct);
            }

            // Process images one at a time (like video component)
            const processedProducts = [];
            for (const entry of contentToSubmit) {
                let url = '';
                if (entry.image) {
                    url = await uploadImage(entry.image);
                    if (!url) {
                        throw new Error('Image cannot contain nudity, violence or drugs');
                    }
                }

                processedProducts.push({
                    type: 'product',
                    sectionTitle: sectionTitle,
                    title: entry.primaryText || '',
                    description: entry.secondaryText || '',
                    link: entry.link || '',
                    avatar: url || entry.avatar,
                    primaryText: entry.primaryText || '',
                    secondaryText: entry.secondaryText || ''
                });
            }

            // Maintain header content
            const existingHeader = custom?.content?.find(item => item.type === 'header');
            
            const dataCopy = {
                ...custom,
                page: custom.page,
                type: 'custom',
                headeractive: true,
                content: [
                    existingHeader || {
                        type: 'header',
                        avatar: custom?.avatar || '',
                        title: custom?.headerText || '',
                        subtitle: custom?.subheaderText || ''
                    },
                    ...processedProducts
                ],
                id: `custom-${new Date().getMilliseconds()}${Math.random().toString(10)}`
            };

            console.log(`payload::`, dataCopy);

            const result = await dispatch(addCustomForFocusThunk({ 
                token, 
                payload: dataCopy 
            })).unwrap();
            
            if (result.status === 200) {
                dispatch(clearFocusSection());
                navigate("/focus-page");
            } else {
                throw new Error(result.message || 'Failed to create focus page');
            }
        } catch (error) {
            console.error('Error creating custom focus page:', error);
            dispatch(setErrorAlert(error?.message || 'Error creating focus page'));
        } finally {
            dispatch(setLoader(false));
        }
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative">
                    <div className="w-full flex flex-col flex-1 overflow-y-auto pb-[120px]">
                        {/* Header Section */}
                        <div className="px-4 my-[50px] flex w-full justify-between fixed w-full sm:w-[390px] bg-black z-20">
                            <p className="text-white">Add content</p>
                            <div className="flex gap-[30px]">
                                {isFormComplete && (
                                    <div className="text-white cursor-pointer" onClick={handleAddAnother}>
                                        Another
                                    </div>
                                )}
                                <div
                                    className={`text-white cursor-pointer ${!isDoneValid() ? 'opacity-50' : ''}`}
                                    onClick={() => isDoneValid() && handleDone()}
                                >
                                    Done
                                </div>
                                <div className="text-white cursor-pointer" onClick={() => navigate(-1)}>
                                    Go back
                                </div>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="w-full px-4 mt-[120px] relative z-10">
                            {/* Section Title Input */}
                            <div className="flex items-center mb-4">
                                <DizeeInput
                                    placeholder="Enter section title (applies to all content)"
                                    onChange={(e) => setSectionTitle(e.target.value)}
                                    value={sectionTitle}
                                />
                            </div>

                            <div className="flex flex-col">
                                {/* Description Textarea */}
                                <div className='flex w-full justify-between items-center mt-5' style={{ fontSize: '12px' }}>
                                    <div className='items-center flex flex-row text-white w-full'>
                                        <textarea
                                            placeholder="Enter description"
                                            value={currentProduct.secondaryText}
                                            onChange={(e) => setCurrentProduct({ 
                                                ...currentProduct, 
                                                secondaryText: e.target.value 
                                            })}
                                            className="w-full bg-transparent text-white placeholder-[rgba(255,255,255,0.5)] px-0 py-3 min-h-[150px] resize-y outline-none text-[12px]"
                                        />
                                    </div>
                                </div>

                                {/* Link Input */}
                                <div className='flex w-full justify-between items-center mt-5' style={{ fontSize: '12px' }}>
                                    <div className='items-center flex flex-row text-white w-full'>
                                        <div className="flex items-center w-full">
                                            <DizeeInput
                                                placeholder="Enter link"
                                                onChange={handleLinkChange}
                                                value={currentProduct.link}
                                            />
                                            {isValidUrl(currentProduct.link) && <Check className="h-[12px] w-[18px] ml-2" />}
                                        </div>
                                    </div>
                                </div>

                                {/* Image Upload */}
                                <div className="w-full mt-5">
                                    <p 
                                        className="text-white text-[12px] mb-2 cursor-pointer"
                                        onClick={() => document.getElementById('file-input').click()}
                                    >
                                        {currentProduct.preview ? 'Change image' : 'Upload image'}
                                    </p>
                                    {currentProduct.preview && (
                                        <div className="w-[50px] h-[50px] rounded-[3px] overflow-hidden">
                                            <img src={currentProduct.preview} alt="preview" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <input
                                        id="file-input"
                                        type="file"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        accept="image/*"
                                    />
                                </div>

                                {/* Added Products List */}
                                {productItems.length > 0 && (
                                    <div className="flex flex-col w-full mt-[40px]">
                                        <div className='pb-[20px] flex w-full items-center'>
                                            <div className='flex flex-row text-white w-full'>
                                                <p>Added Products</p>
                                            </div>
                                        </div>
                                        {productItems.map((item, index) => (
                                            <AddedCustom
                                                key={index}
                                                item={item}
                                                setOverlayVisible={setIsOverlayVisible}
                                                setItemForBackend={(item) => {
                                                    setSelectedProduct(item);
                                                    setSelectedIndex(index);
                                                    setIsOverlayVisible(true);
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Replace the existing footer with this new structure */}
                    <div className="w-full absolute bottom-0 left-0">
                        <div className="w-full h-[20px] bg-gradient-to-t from-black to-transparent" />
                        <div className="w-full bg-black px-4 py-6">
                            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                <p>Add your product content to display in your focus page.</p>
                                <p>Only an image is required. Add title, description and link to enhance the presentation.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Move overlay outside main container */}
            {isOverlayVisible && (
                <OverlayCustom
                    isOverlayVisible={isOverlayVisible}
                    setOverlayVisible={setIsOverlayVisible}
                    selectedItem={selectedProduct}
                    handleEdit={handleEditProduct}
                    handleDelete={handleDeleteProduct}
                />
            )}
        </LayoutHeader>
    );
} 